import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import useSEO from '../hooks/useSEO';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  FiUser, FiTarget, FiHeart, FiAward, FiStar, FiCheckCircle, 
  FiUsers, FiTrendingUp, FiShield, FiClock 
} from 'react-icons/fi';
import { getImageUrl } from '../utils/imageUtils';
import { getPresignedUrl } from '../utils/api';

const About = () => {
  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    'name': 'Deeksha Rajput',
    'jobTitle': 'Certified Expert & Clinical Nutritionist',
    'image': 'https://dfitclub.com/favicon.png',
    'worksFor': {
      '@type': 'Organization',
      'name': 'Dfitclub',
      'url': 'https://dfitclub.com'
    },
    'description': 'Certified expert clinical nutritionist and dietitian with over 5 years of experience in lifestyle coaching and clinical nutrition management.',
    'sameAs': [
      'https://www.instagram.com/d_fitclub_/',
      'https://www.facebook.com/share/1Axj8uhKsP/?mibextid=wwXIfr',
      'https://www.youtube.com/@fit_with_Deeksha'
    ]
  };

  useSEO({
    title: 'Meet Deeksha Rajput | Clinical Nutritionist',
    description: 'Learn more about Deeksha Rajput, founder of Dfitclub. Over 5 years of clinical nutrition experience, helping you achieve sustainable weight loss, weight gain, and health transformations.',
    keywords: 'about Deeksha Rajput, dietitian India, clinical nutritionist, weight management coach, Dfitclub founder',
    schema: aboutSchema
  });

  const [heroRef, heroInView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [founderRef, founderInView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [valuesRef, valuesInView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [imageError, setImageError] = useState(false);
  const [founderImageUrl, setFounderImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  
  // Get founder image URL/key from environment variable
  const founderImageEnv = process.env.REACT_APP_FOUNDER_IMAGE_URL || process.env.REACT_APP_FOUNDER_IMAGE_KEY || null;
  
  // Fetch presigned URL for private S3 images
  useEffect(() => {
    const fetchPresignedUrl = async () => {
      if (!founderImageEnv) {
        console.log('ℹ️ No founder image configured in environment variables');
        setFounderImageUrl(null);
        return;
      }
      
      console.log('🖼️ Founder image env value:', founderImageEnv);
      setImageLoading(true);
      try {
        // Check if it's an S3 key (no http/https) or S3 URL
        if (founderImageEnv.startsWith('http://') || founderImageEnv.startsWith('https://')) {
          // It's a full S3 URL - extract the key and get presigned URL
          if (founderImageEnv.includes('s3.amazonaws.com')) {
            const s3Key = founderImageEnv.split('.amazonaws.com/')[1];
            console.log('🔑 Extracted S3 key:', s3Key);
            if (s3Key) {
              console.log('🔐 Fetching presigned URL for key:', s3Key);
              const presignedUrl = await getPresignedUrl(s3Key);
              console.log('✅ Got presigned URL:', presignedUrl ? 'Success' : 'Failed');
              setFounderImageUrl(presignedUrl);
            } else {
              console.warn('⚠️ Could not extract S3 key from URL, using URL directly');
              // If extraction fails, try using the URL directly (might be public)
              setFounderImageUrl(founderImageEnv);
            }
          } else {
            // Not an S3 URL, use as-is
            console.log('ℹ️ Not an S3 URL, using as-is:', founderImageEnv);
            setFounderImageUrl(founderImageEnv);
          }
        } else if (founderImageEnv.startsWith('s3://')) {
          // S3 URI format - extract key
          const s3Key = founderImageEnv.replace(/^s3:\/\/[^\/]+\//, '');
          console.log('🔑 Extracted S3 key from URI:', s3Key);
          const presignedUrl = await getPresignedUrl(s3Key);
          console.log('✅ Got presigned URL:', presignedUrl ? 'Success' : 'Failed');
          setFounderImageUrl(presignedUrl);
        } else {
          // It's an S3 key (e.g., "uploads/founder/image.png")
          console.log('🔑 Using as S3 key:', founderImageEnv);
          console.log('🔐 Fetching presigned URL for key:', founderImageEnv);
          const presignedUrl = await getPresignedUrl(founderImageEnv);
          console.log('✅ Got presigned URL:', presignedUrl ? 'Success' : 'Failed');
          setFounderImageUrl(presignedUrl);
        }
      } catch (error) {
        console.error('❌ Failed to get presigned URL for founder image:', error);
        console.error('❌ Error details:', error.response || error.message);
        // Fallback: try using the URL/key directly
        console.log('⚠️ Falling back to using URL/key directly');
        setFounderImageUrl(founderImageEnv);
      } finally {
        setImageLoading(false);
      }
    };
    
    fetchPresignedUrl();
  }, [founderImageEnv]);

  const achievements = [
    { icon: FiUsers, value: '500+', label: 'Lives Transformed' },
    { icon: FiStar, value: '4.8/5', label: 'Client Rating' },
    { icon: FiTarget, value: '95%', label: 'Success Rate' },
    { icon: FiClock, value: '5+', label: 'Years Experience' }
  ];

  const values = [
    {
      icon: FiHeart,
      title: 'Compassionate Care',
      description: 'We understand that every health journey is personal and unique. Our approach is built on empathy, understanding, and genuine care for each client.'
    },
    {
      icon: FiShield,
      title: 'Science-Based Approach',
      description: 'All our programs are grounded in evidence-based nutrition science and proven methodologies for sustainable health transformation.'
    },
    {
      icon: FiCheckCircle,
      title: 'Sustainable Results',
      description: 'We focus on creating lasting lifestyle changes rather than quick fixes, ensuring long-term success and wellbeing.'
    },
    {
      icon: FiTrendingUp,
      title: 'Continuous Support',
      description: '24/7 guidance and motivation throughout your journey. We are committed to your success every step of the way.'
    }
  ];

  const qualifications = [
    'Certified Nutritionist & Dietitian',
    'Masters in Clinical Nutrition',
    'Specialized in Weight Management',
    'Certified Lifestyle Coach',
    'Sports Nutrition Certification',
    '5+ Years Clinical Experience'
  ];

  const approach = [
    {
      step: '01',
      title: 'Comprehensive Assessment',
      description: 'Detailed analysis of your health history, lifestyle, goals, and preferences to create a personalized foundation.'
    },
    {
      step: '02',
      title: 'Customized Plan Creation',
      description: 'Development of a tailored nutrition and lifestyle plan that fits your specific needs and circumstances.'
    },
    {
      step: '03',
      title: 'Ongoing Support & Monitoring',
      description: 'Regular check-ins, progress tracking, and plan adjustments to ensure consistent progress toward your goals.'
    },
    {
      step: '04',
      title: 'Lifestyle Integration',
      description: 'Gradual integration of healthy habits into your daily routine for sustainable long-term results.'
    }
  ];

  return (
          <div className="min-h-screen">
      {/* Hero Section */}
      <section ref={heroRef} className="section-padding bg-gradient-to-br from-primary-50 to-white">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={heroInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <h1 className="heading-hero text-gray-900 mb-6">
                About <span className="text-visible-orange">DFITCLUB</span>
              </h1>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                Founded by certified nutritionist Deeksha Rajput, Dfitclub is dedicated to transforming lives 
                through personalized nutrition guidance and sustainable lifestyle changes.
              </p>
              <p className="text-base text-gray-600 leading-relaxed mb-8">
                Our mission is to make healthy living accessible, achievable, and enjoyable for everyone, 
                regardless of their starting point or background.
              </p>
              <Link to="/contact" className="btn-primary">
                Start Your Journey
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={heroInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative flex items-center justify-center"
            >
              <div className="w-full max-w-md mx-auto bg-white rounded-3xl p-8 md:p-12 shadow-2xl ring-4 ring-primary-100 flex items-center justify-center">
                <img 
                  src="/images/logo/logo.png" 
                  alt="DFITCLUB Logo"
                  className="w-full h-auto max-w-xs object-contain"
                  style={{
                    maxHeight: '300px'
                  }}
                  onError={(e) => {
                    console.error('Failed to load logo image');
                    // Fallback to SVG if PNG fails
                    e.target.src = '/images/logo/sample-logo.svg';
                  }}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-lg mx-auto">
            {achievements.map((achievement, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={heroInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-extrabold text-visible-orange mb-1">{achievement.value}</div>
                <div className="text-xs text-gray-600">{achievement.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section ref={founderRef} className="section-padding bg-gray-50">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={founderInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              {imageLoading ? (
                <div className="w-full aspect-[3/4] max-w-sm md:max-w-md mx-auto bg-gradient-to-br from-accent-200 to-accent-300 rounded-3xl flex items-center justify-center">
                  <div className="text-center text-accent-800">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-800 mx-auto mb-4"></div>
                    <p className="text-sm">Loading image...</p>
                  </div>
                </div>
              ) : founderImageUrl && !imageError ? (
                <div className="w-full aspect-[3/4] max-w-sm md:max-w-md mx-auto rounded-3xl overflow-hidden shadow-2xl ring-4 ring-accent-100 relative">
                  <img 
                    src={founderImageUrl} 
                    alt="Deeksha Rajput - Certified Expert & Clinical Nutritionist"
                    className="absolute inset-0 w-full h-full object-cover object-center"
                    onError={(e) => {
                      console.error('Failed to load founder image:', founderImageUrl);
                      setImageError(true);
                    }}
                  />
                </div>
              ) : (
                <div className="w-full aspect-[3/4] max-w-sm md:max-w-md mx-auto bg-gradient-to-br from-accent-200 to-accent-300 rounded-3xl flex items-center justify-center">
                  <div className="text-center text-accent-800">
                    <FiAward className="w-24 h-24 mx-auto mb-4" />
                    <p className="text-lg font-medium">Certified Expert</p>
                    <p className="text-sm">Clinical Nutritionist</p>
                  </div>
                </div>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={founderInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h2 className="heading-section text-gray-900 mb-6">
                Meet <span className="text-visible-orange">Deeksha Rajput</span>
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                With over 5 years of clinical experience and a passion for transforming lives, Deeksha founded 
                Dfitclub to bridge the gap between scientific nutrition knowledge and practical, sustainable lifestyle changes.
              </p>
              <p className="text-base text-gray-600 leading-relaxed mb-8">
                Her approach combines evidence-based nutrition science with personalized care, ensuring that each 
                client receives the individual attention they deserve on their health journey.
              </p>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-secondary-900">Qualifications & Certifications:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {qualifications.map((qualification, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <FiCheckCircle className="w-5 h-5 text-primary-600 flex-shrink-0" />
                      <span className="text-secondary-700">{qualification}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section ref={valuesRef} className="section-padding bg-white">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={valuesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 leading-tight mb-4">
              Our <span className="text-visible-orange">Values</span>
            </h2>
            <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
              The principles that guide everything we do at Dfitclub
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={valuesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="card p-8"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mb-6">
                  <value.icon className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-semibold text-secondary-900 mb-4">{value.title}</h3>
                <p className="text-secondary-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="section-padding bg-gray-50">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 leading-tight mb-4">
              Our <span className="text-visible-orange">Approach</span>
            </h2>
            <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
              A systematic, personalized methodology for sustainable health transformation
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {approach.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="relative"
              >
                <div className="card p-6 text-center h-full">
                  <div className="w-12 h-12 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-lg font-bold">
                    {step.step}
                  </div>
                  <h3 className="text-lg font-semibold text-secondary-900 mb-3">{step.title}</h3>
                  <p className="text-secondary-600 text-sm">{step.description}</p>
                </div>
                {index < approach.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 text-primary-300">
                    <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="card p-8"
            >
              <FiTarget className="w-12 h-12 text-primary-600 mb-6" />
              <h3 className="text-2xl font-bold text-secondary-900 mb-4">Our Mission</h3>
              <p className="text-secondary-600">
                To empower individuals to achieve their health goals through personalized nutrition guidance, 
                evidence-based strategies, and unwavering support. We believe that everyone deserves access to 
                quality nutrition guidance that fits their unique lifestyle and preferences.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="card p-8"
            >
              <FiStar className="w-12 h-12 text-primary-600 mb-6" />
              <h3 className="text-2xl font-bold text-secondary-900 mb-4">Our Vision</h3>
              <p className="text-secondary-600">
                To create a world where healthy living is accessible, enjoyable, and sustainable for everyone. 
                We envision a future where individuals are empowered with the knowledge and support they need 
                to make informed decisions about their health and wellbeing.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-primary-100 to-primary-200 text-neutral-800">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className="heading-section text-gray-900 mb-4">
              Ready to Transform Your Health?
            </h2>
            <p className="text-section-body mb-6">
              Join our community of successful clients and start your personalized health journey today
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-accent">
                Enroll Now
              </Link>
              <Link to="/transformations" className="btn-secondary">
                See Success Stories
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;